/**
 * Logistics overview.
 *
 * Four periods of the same question, and every figure comes from the deployed
 * report endpoint. Nothing here is computed in the browser and nothing from the
 * reference workbook is baked in — a hard-coded total is a number that stops
 * being true the next time somebody rides.
 *
 * The period tabs refetch rather than recompute, because "this year" over a
 * growing ledger is a database aggregate and not something to assemble from
 * four smaller answers in JavaScript.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, EmptyState, ErrorState, Spinner, cn } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { money } from '../../lib/format';
import { Stat, SectionTitle } from './parts';
import { useLogisticsReport, useSettlementTotals, useUnsettled } from './data';
import { LocationScope } from '../../components/LocationScope';
import { useLocationFilter, useLocationReady } from '../../lib/locationContext';

type Period = 'today' | 'week' | 'month' | 'year';

const PERIODS: Array<{ key: Period; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
];

/**
 * The window each period covers, in the business's own timezone.
 *
 * Weeks start on Sunday, which is how CenoHub's own books have always run —
 * every one of the staging workbook's period keys falls on a Sunday. Using the
 * ISO Monday here would put a Saturday's rides in the wrong week and no figure
 * would ever match what the owner counted.
 */
function windowFor(period: Period, timezone: string): { from: string; to: string } {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const day = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

  if (period === 'today') return { from: iso(day), to: iso(day) };
  if (period === 'week') {
    const start = new Date(day);
    start.setUTCDate(start.getUTCDate() - start.getUTCDay()); // Sunday
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    return { from: iso(start), to: iso(end) };
  }
  if (period === 'month') {
    return {
      from: iso(new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1))),
      to: iso(new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0))),
    };
  }
  return {
    from: iso(new Date(Date.UTC(now.getFullYear(), 0, 1))),
    to: iso(new Date(Date.UTC(now.getFullYear(), 11, 31))),
  };
}

/**
 * Channel labels.
 *
 * `cenox` is a delivery somebody typed on a CenoX screen rather than one that
 * arrived through the Shop, the App or WhatsApp — a real channel in its own
 * right, not a fallback.
 */
const SOURCE_LABELS: Record<string, string> = {
  shop: 'Shop',
  app: 'App',
  whatsapp: 'WhatsApp',
  cenox: 'Entered in CenoX',
  import: 'Imported, channel not recorded',
  '(unknown)': 'Channel not recorded',
};

const ORIGIN_LABELS: Record<string, string> = {
  live: 'Recorded as it happened',
  import: 'Imported from a sheet',
};

export function LogisticsOverviewPage(): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const timezone = business?.timezone ?? 'Africa/Lagos';

  const [period, setPeriod] = useState<Period>('today');
  const range = useMemo(() => windowFor(period, timezone), [period, timezone]);

  // Day grain for Today, so remittance correctly reads as not applicable;
  // the coarser periods get their own grain and carry it.
  const grain = period === 'today' ? 'day' : period === 'week' ? 'week' : period === 'month' ? 'month' : 'year';

  // The sidebar's Country → State/Region → Location, as on every screen.
  const scope = useLocationFilter();
  const report = useLogisticsReport({ ...range, grain, dimension: 'source', ...scope }, useLocationReady());
  const unsettled = useUnsettled(range);
  // Aggregated server-side; a page sum would understate it. Not date-filtered:
  // an unpaid settlement from last month is still owed today.
  const owing = useSettlementTotals({ status: 'unpaid' });

  if (report.isError) {
    return (
      <ErrorState
        message="Could not load the Logistics overview."
        onRetry={() => void report.refetch()}
      />
    );
  }

  const t = report.data?.totals;
  /** A range holding no whole remittance period has none to report. */
  const hasWholePeriod = (report.data?.periods.length ?? 0) > 0;
  const unsettledRides = unsettled.data?.data.reduce((sum, r) => sum + r.rides, 0) ?? null;

  return (
    <div className="space-y-5">
      {/* Rider settlements are owed by rider, not by location: they stay whole-business. */}
      <LocationScope testId="logistics-scope" note="rider settlements cover every location" />
      <nav className="flex gap-1 overflow-x-auto" aria-label="Period">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPeriod(p.key)}
            aria-current={period === p.key ? 'true' : undefined}
            className={cn(
              'whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
              period === p.key
                ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200'
                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800',
            )}
          >
            {p.label}
          </button>
        ))}
      </nav>

      {report.isPending ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <>
          <section>
            <SectionTitle
              aside={
                <span className="text-xs text-slate-400">
                  {range.from === range.to ? range.from : `${range.from} – ${range.to}`}
                </span>
              }
            >
              Deliveries and money
            </SectionTitle>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
              <Stat label="Deliveries" count={t?.deliveries ?? 0} hint="every status" />
              <Stat
                label="Gross delivery fees"
                value={t?.gross}
                currency={currency}
                hint="completed and paid"
              />
              <Stat
                label="Company revenue"
                value={t?.companyShare}
                currency={currency}
                tone="good"
                hint="commission"
              />
              <Stat label="Rider earnings" value={t?.riderPayout} currency={currency} />
              <Stat
                label="Remittance"
                /* Shown when the range holds at least one whole period, which
                   is the only time the server reports a figure for it at all.
                   Today holds none, so it reads "—" — not applicable, not
                   zero. See the note in ReportsPage. */
                value={hasWholePeriod ? t?.remittance : null}
                currency={currency}
                hint={hasWholePeriod ? undefined : 'per period, not per day'}
              />
              <Stat
                label="Total revenue"
                value={hasWholePeriod ? t?.totalRevenue : null}
                currency={currency}
                tone="good"
                hint="commission + remittance"
              />
              <Stat
                label="Fees, all statuses"
                value={t?.grossAllStatuses}
                currency={currency}
                hint="includes cancelled"
              />
              <Stat
                label="Counted as revenue"
                count={t?.financiallyComplete ?? 0}
                hint={`of ${t?.deliveries ?? 0} deliveries`}
              />
            </div>
          </section>

          {/*
            Remittance is a period obligation and does not come from the
            deliveries, so a range can carry accrued remittance with no rides
            behind it — which is exactly the case while the historical books are
            configured but not yet imported. Said out loud, because a figure that
            large beside a small commission invites the wrong conclusion.
          */}
          {report.data && report.data.periods.length > 0 && t && Number(t.remittance) > 0 &&
           Number(t.companyShare) < Number(t.remittance) ? (
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {money(t.remittance, currency)} of this is remittance from{' '}
              {report.data.periods.length}{' '}
              {report.data.periods.length === 1 ? 'period' : 'periods'} — a flat weekly charge that
              does not come from the deliveries, so it stands whether or not their rides are in
              CenoX yet.
            </p>
          ) : null}

          <section>
            <SectionTitle>Riders and settlement</SectionTitle>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              <Stat
                label="CenoHub owes riders"
                value={owing.data?.cenohubOwesRider}
                currency={currency}
                tone="good"
                hint="across unpaid settlements"
              />
              <Stat
                label="Riders owe CenoHub"
                value={owing.data?.riderOwesCenohub}
                currency={currency}
                tone="owed"
                hint="company share held in cash"
              />
              <Stat
                label="Unsettled deliveries"
                count={unsettledRides ?? 0}
                hint="not on a settlement yet"
              />
              <Stat label="Unpaid settlements" count={owing.data?.settlements ?? 0} />
            </div>
            <p className="mt-2 text-xs text-slate-400">
              <Link to="/business/logistics/settlements" className="text-brand-700 hover:underline">
                Open settlements
              </Link>{' '}
              to see each rider&rsquo;s two directions in full.
            </p>
          </section>

          <section>
            <SectionTitle>Source split</SectionTitle>
            {report.data && report.data.sources.length > 0 ? (
              <Card>
                <ul className="divide-y divide-slate-100">
                  {report.data.sources.map((s) => (
                    <li key={s.source} className="flex items-center justify-between gap-4 py-2">
                      <span className="text-sm text-slate-700">
                        {SOURCE_LABELS[s.source] ?? s.source}
                      </span>
                      <span className="flex items-baseline gap-4">
                        <span className="text-sm tabular-nums text-slate-500">
                          {s.deliveries} {s.deliveries === 1 ? 'delivery' : 'deliveries'}
                        </span>
                        <span className="w-28 text-right text-sm tabular-nums text-slate-900">
                          {money(s.gross, currency)}
                        </span>
                        <span className="w-24 text-right text-sm font-medium tabular-nums text-emerald-700">
                          {money(s.companyShare, currency)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-slate-400">
                  Fees, then company revenue. The App and WhatsApp take a percentage of the fee; the
                  Shop takes a fixed amount.
                </p>
              </Card>
            ) : (
              <EmptyState title="No deliveries in this period" />
            )}
          </section>

          <section>
            <SectionTitle>Live and imported</SectionTitle>
            {report.data && report.data.origins.length > 0 ? (
              <Card>
                <ul className="divide-y divide-slate-100">
                  {report.data.origins.map((o) => (
                    <li key={o.origin} className="flex items-center justify-between gap-4 py-2">
                      <span className="text-sm text-slate-700">
                        {ORIGIN_LABELS[o.origin] ?? o.origin}
                      </span>
                      <span className="flex items-baseline gap-4">
                        <span className="text-sm tabular-nums text-slate-500">
                          {o.deliveries} {o.deliveries === 1 ? 'delivery' : 'deliveries'}
                        </span>
                        <span className="w-28 text-right text-sm font-medium tabular-nums text-slate-900">
                          {money(o.gross, currency)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}
          </section>

          {(t?.unmappedRiders ?? 0) > 0 || (t?.anomalies ?? 0) > 0 ? (
            <section>
              <SectionTitle>Needs a look</SectionTitle>
              <div className="grid gap-2.5 sm:grid-cols-2">
                {(t?.unmappedRiders ?? 0) > 0 ? (
                  <Link
                    to="/business/logistics/riders"
                    className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 transition-colors hover:bg-amber-50"
                  >
                    <p className="text-sm font-medium text-amber-900">
                      {t!.unmappedRiders} rider {t!.unmappedRiders === 1 ? 'name' : 'names'} unmapped
                    </p>
                    <p className="mt-0.5 text-xs text-amber-700">
                      Their rides count, but they accrue no remittance until mapped.
                    </p>
                  </Link>
                ) : null}
                {(t?.anomalies ?? 0) > 0 ? (
                  <Link
                    to="/business/logistics/deliveries?anomaly=1"
                    className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 transition-colors hover:bg-amber-50"
                  >
                    <p className="text-sm font-medium text-amber-900">
                      {t!.anomalies} {t!.anomalies === 1 ? 'delivery' : 'deliveries'} flagged
                    </p>
                    <p className="mt-0.5 text-xs text-amber-700">
                      The commission rule and the recorded figures disagree.
                    </p>
                  </Link>
                ) : null}
              </div>
            </section>
          ) : null}

          <section>
            <SectionTitle>Returns</SectionTitle>
            <EmptyState
              title="Returns are not tracked yet"
              description="Refunds, processor fees and platform cost are their own piece of work, kept out of commission on purpose."
            />
          </section>
        </>
      )}
    </div>
  );
}
